import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { getCar, fetchCarsByPlate, getKanbanRegua } from '../../../api/carsApi';
import { notify } from '../../../shared/feedback/notify';
import { queryKeys } from '../../../infrastructure/react-query/queryKeys';
import { useEsteiras } from '../../../hooks/useEsteiras';
import { corDaClasse, nomeDaClasse } from '../../../shared/ui/cor-da-classe';
import { derivarReguaDoVeiculo, montarBaseDaRegua } from '../../../views/home/regua.util';
import ReguaEsteiras from '../../regua-esteiras/ReguaEsteiras';
import type { ServicoInacabado } from '../../../interfaces/AtividadeCarro';

import ObservacoesAtendimento from '../../observacoes-atendimento/ObservacoesAtendimento';
import ProgressoSubtarefas from '../../progresso-subtarefas/ProgressoSubtarefas';
import ComentariosDoVeiculo from '../../comentarios/ComentariosDoVeiculo';
import TagsDaEsteira from '../../tags-da-esteira/TagsDaEsteira';
import SeloPosEvento from '../../selo-pos-evento/SeloPosEvento';
import SeloPrazoDaEtapa from '../../selo-prazo/SeloPrazoDaEtapa';
import SeloEsteiraParalela from '../../selo-esteira-paralela/SeloEsteiraParalela';
import ControleParalelasModal from '../controle-paralelas/ControleParalelasModal';
import { EsteiraParalelaDoCarro, PosEventoInfo } from '../../../interfaces/Car';

import '../../../shared/feedback/erro-de-tela.css';
import './CarHomeDetailsModal.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

interface Car {
  plate: string;
  /** at-89: esteira atual — usada para achar o motivo da pendência que vale agora. */
  status?: string | null;
  eventNumber: string;
  budgetValue: string;
  responsibleName: string;
  returnCount: number;
  /** at-54: texto livre do atendimento, exibido quando preenchido. */
  observacoesCliente?: string | null;
  observacoesOficina?: string | null;
  /** at-96: preenchido ⇒ retorno de garantia (alimenta o selo ao lado da placa). */
  posEvento?: PosEventoInfo | null;
  /** at-99: esteiras abertas em paralelo (alimenta o selo "+ Mecânica" ao lado da placa). */
  esteirasParalelas?: EsteiraParalelaDoCarro[];
  /** at-99: a etapa do veículo, para o controle das paralelas contrapor uma à outra. */
  esteira?: { id: string; nome: string };
  // at-130: o que o card do kanban mostra, agora reproduzido no topo do modal.
  esteiraId?: string | null;
  model?: string | null;
  color?: string | null;
  carClass?: string | null;
  releaseStatus?: 'TOTAL' | 'PARCIAL' | 'PENDENTE' | null;
  releasedAt?: string | null;
  returnedToWaiting?: boolean | null;
  daysToAuthorize?: number | null;
  externalShopDays?: number | null;
  daysInShop?: number | null;
  deliveryForecast?: number | null;
  estimatedDaysInStatus?: number | null;
  servicosInacabados?: ServicoInacabado[] | null;
}

interface Visita {
  id: string;
  eventNumber?: string | null;
  entryDate?: string | null;
  status?: string | null;
  statusLabel?: string | null;
}

interface CarHomeDetailsModalProps {
  isOpen: boolean;
  carId: string | null;
  onCancel: () => void;
  sidebarRef?: React.RefObject<HTMLDivElement>;
  isSidebarOpen: boolean;
  /**
   * at-130: fecha sozinho depois de N segundos. Só o Telão em tela cheia passa — na TV ninguém
   * fecha o modal, e o quadro ficaria escondido. Ausente, o modal não fecha sozinho.
   */
  fecharEmSegundos?: number;
}

const valorOuNA = (v?: number | null) => (v === null || v === undefined ? 'N/A' : v);

/**
 * Consulta do atendimento a partir do Painel Geral (Home).
 *
 * at-82 (24/08/2026): reescrito. O markup vinha do export do Figma (`Frame1000004021`,
 * `Lta2456`, `Fotos`…) e três dessas classes **não existiam em CSS nenhum** — o título e a
 * placa saíam como texto solto, e a "tabela" era um cabeçalho laranja com os valores
 * desalinhados embaixo. Junto saiu a seção **Imagens**, que exibia a moldura cinza e as duas
 * setas mesmo sem foto alguma: a feature de fotos do veículo foi removida da aplicação
 * (a recepção registra as imagens pelo checklist).
 */
const CarHomeDetailsModal: React.FC<CarHomeDetailsModalProps> = ({
  isOpen,
  carId,
  onCancel,
  sidebarRef,
  fecharEmSegundos,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [restante, setRestante] = useState<number | null>(null);

  // at-130: a régua com TODAS as esteiras ativas — mesma rota do kanban, com um id só.
  const { esteirasAtivas } = useEsteiras();
  const reguaQuery = useQuery({
    queryKey: queryKeys.cars.kanbanRegua(carId ? [carId] : []),
    queryFn: () => getKanbanRegua(carId ? [carId] : []),
    enabled: isOpen && !!carId,
    staleTime: 30_000,
  });
  const [car, setCar] = useState<Car | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [history, setHistory] = useState<Visita[]>([]);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);
  // at-99: o selo ao lado da placa abre o controle das paralelas (pedido do Victor, 10/09/2026).
  const [controleParalelasAberto, setControleParalelasAberto] = useState(false);

  const fetchCarData = async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const carData = await getCar(id);
      setCar(carData);

      if (carData?.plate) {
        setHistoryLoading(true);
        try {
          const visits = await fetchCarsByPlate(carData.plate);
          setHistory(visits);
        } catch (hErr) {
          // Sem sinal, o veículo parecia não ter visitas anteriores.
          notify.errorFrom(
            hErr,
            'Não foi possível carregar o histórico de visitas desta placa. Recarregue a página; se persistir, acione a TI.',
          );
        } finally {
          setHistoryLoading(false);
        }
      }
    } catch (err) {
      setError('Não foi possível carregar os dados deste veículo. Recarregue a página; se persistir, acione a TI.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && carId) {
      setHistory([]);
      fetchCarData(carId);
    }
  }, [isOpen, carId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // ⚠️ Modal FILHO aberto sobre este entra na guarda: ele é renderizado fora do `ref` daqui,
      // então o clique dentro dele contaria como clique de fundo e derrubaria os dois.
      if (controleParalelasAberto) return;

      const clickedNode = event.target as Node;
      const isOutsideModal = modalRef.current && !modalRef.current.contains(clickedNode);
      // at-130: sem sidebar (o Telão em tela cheia não tem menu), qualquer clique fora fecha.
      // Antes, sem `sidebarRef`, esta condição era sempre falsa e o clique fora não fazia nada.
      const isOutsideSidebar = !sidebarRef?.current || !sidebarRef.current.contains(clickedNode);

      if (isOutsideModal && isOutsideSidebar) {
        onCancel();
      }
    };
    // at-130: Esc fecha (na TV o teclado é o caminho mais curto). Mesma guarda do clique fora.
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !controleParalelasAberto) onCancel();
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEsc);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onCancel, sidebarRef, controleParalelasAberto]);

  // at-130: contagem para fechar sozinho (só quando `fecharEmSegundos` vem — Telão em tela cheia).
  // Pausa enquanto o controle das paralelas, aberto por cima, estiver em uso.
  useEffect(() => {
    if (!isOpen || !fecharEmSegundos) {
      setRestante(null);
      return undefined;
    }
    setRestante(fecharEmSegundos);
    return undefined;
  }, [isOpen, fecharEmSegundos, carId]);

  useEffect(() => {
    if (restante === null || controleParalelasAberto) return undefined;
    if (restante <= 0) {
      onCancel();
      return undefined;
    }
    const t = setTimeout(() => setRestante((r) => (r === null ? null : r - 1)), 1000);
    return () => clearTimeout(t);
  }, [restante, controleParalelasAberto, onCancel]);

  const chipsDaRegua = useMemo(() => {
    const item = reguaQuery.data?.find((i) => i.carId === carId);
    if (!car || !item) return null;
    const esteiras = esteirasAtivas.filter((e) => e.chave !== 'ENTREGA');
    return derivarReguaDoVeiculo(car, montarBaseDaRegua(esteiras, item), item.planejadas ?? []);
  }, [car, carId, reguaQuery.data, esteirasAtivas]);

  if (!isOpen) return null;

  const visitasAnteriores = history.filter((h) => h.id !== carId);
  const formatarData = (valor?: string | null) => {
    if (!valor) return '-';
    const data = new Date(valor);
    return Number.isNaN(data.getTime()) ? '-' : data.toLocaleDateString('pt-BR');
  };

  return (
    <div className="add-car-modal-overlay">
      <div className="chd-modal" ref={modalRef}>
        <div className="chd-cabecalho">
          <div>
            <h2 className="chd-titulo">Informações do atendimento</h2>
            <p className="chd-placa">
              Placa do veículo: <strong>{car?.plate ?? '-'}</strong>{' '}
              {/* at-89: motivo da pendência ao lado da placa (Victor, 03/09/2026).
                  A esteira vem do card do kanban — este modal projeta só o que exibe. */}
              {/* Só depois de o veículo carregar: com a carga falhando, o selo competiria com
                  o aviso de erro e o usuário leria "motivo" de um veículo que não veio. */}
              {car && carId && (
                <TagsDaEsteira carId={carId} esteiraAtual={car.status} variante="inline" />
              )}
              {/* at-96: retorno de garantia, ao lado da placa. */}
              <SeloPosEvento posEvento={car?.posEvento} />
              <SeloPrazoDaEtapa car={car} />
              {/* at-99: há serviço em curso em outra esteira além da etapa do veículo. */}
              <SeloEsteiraParalela
                esteirasParalelas={car?.esteirasParalelas}
                onClick={() => setControleParalelasAberto(true)}
              />
            </p>
          </div>
          <button type="button" className="close-button" onClick={onCancel} aria-label="Fechar">
            &times;
          </button>
        </div>

        <div className="chd-corpo">
          {error && (
            <div className="erro-de-tela" role="alert">
              {error}
            </div>
          )}

          {loading ? (
            <p className="chd-vazio">Carregando...</p>
          ) : car ? (
            <>
              {/* at-130: o card do quadro e a régua com TODAS as esteiras (pedido do Victor,
                  22/10/2026: "deve ter as informações do Kanban antigo"). */}
              <div className="chd-quadro">
                <div className="chd-card">
                  <div className="chd-card-linha">
                    <span className="chd-card-placa">{car.plate}</span>
                    <span
                      className="chd-card-classe"
                      style={{ background: corDaClasse(car.carClass).fundo, color: corDaClasse(car.carClass).texto }}
                    >
                      {nomeDaClasse(car.carClass)}
                    </span>
                  </div>
                  <div className="chd-card-linha">
                    <span className="chd-card-modelo">
                      {[car.model, car.color].filter(Boolean).join(' - ') || 'Modelo não informado'}
                    </span>
                    {car.releaseStatus === 'TOTAL' || (!car.releaseStatus && car.releasedAt) ? (
                      <span className="chd-card-selo chd-card-selo--liberado">Liberado</span>
                    ) : car.releaseStatus === 'PARCIAL' ? (
                      <span className="chd-card-selo chd-card-selo--atencao">Liberação parcial</span>
                    ) : car.returnedToWaiting ? (
                      <span className="chd-card-selo chd-card-selo--atencao">Retorno para ag.</span>
                    ) : null}
                  </div>
                  <dl className="chd-card-campos">
                    <div>
                      <dt>Colaborador responsável:</dt>
                      <dd title={car.responsibleName || 'Nenhum colaborador vinculado'}>
                        {car.responsibleName || 'Nenhum colaborador vinculado'}
                      </dd>
                    </div>
                    <div><dt>Dias para autorização:</dt><dd>{valorOuNA(car.daysToAuthorize)}</dd></div>
                    <div><dt>Dias em oficina externa:</dt><dd>{valorOuNA(car.externalShopDays)}</dd></div>
                    <div><dt>Dias em oficina:</dt><dd>{valorOuNA(car.daysInShop)}</dd></div>
                    <div><dt>Previsão de entrega:</dt><dd>{valorOuNA(car.deliveryForecast)}</dd></div>
                    <div><dt>Previsão de dias no setor:</dt><dd>{valorOuNA(car.estimatedDaysInStatus)}</dd></div>
                  </dl>
                </div>
                <div className="chd-card">
                  <span className="chd-card-titulo">Esteiras</span>
                  {reguaQuery.isError ? (
                    // Régua vazia seria lida como "o veículo não passou por nada" (at-60).
                    <p className="regua-aviso" role="alert">
                      Não foi possível carregar a situação das esteiras deste veículo. Feche e abra de novo; se
                      persistir, acione a TI.
                    </p>
                  ) : chipsDaRegua ? (
                    <ReguaEsteiras chips={chipsDaRegua} comLegenda />
                  ) : (
                    <p className="chd-vazio">Carregando esteiras...</p>
                  )}
                </div>
              </div>

              <div className="chd-tabela-wrapper">
                <table className="chd-tabela">
                  <thead>
                    <tr>
                      <th>Número do evento</th>
                      <th>Colaboradores</th>
                      <th>Retornos à oficina</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td title={car.eventNumber || 'Sem evento'}>{car.eventNumber || 'Sem evento'}</td>
                      <td title={car.responsibleName || 'Nenhum colaborador vinculado'}>
                        {car.responsibleName || 'Nenhum colaborador vinculado'}
                      </td>
                      <td>{car.returnCount ?? 0}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* at-54: texto livre do atendimento (componente compartilhado; some quando vazio) */}
              <ObservacoesAtendimento
                observacoesCliente={car.observacoesCliente}
                observacoesOficina={car.observacoesOficina}
              />

              {/* at-89: o que já foi feito no veículo (some quando não há sub-tarefas).
                  Usa a prop `carId`, não `car.id`: o tipo local do modal projeta só o que a
                  tela exibe e não carrega o id. */}
              {carId && <ProgressoSubtarefas carId={carId} />}
              {carId && <ComentariosDoVeiculo carId={carId} />}
            </>
          ) : (
            !error && <p className="chd-vazio">Nenhum dado disponível.</p>
          )}

          <section className="chd-secao">
            <h3 className="chd-subtitulo">Histórico de atendimentos</h3>
            {historyLoading ? (
              <p className="chd-vazio">Carregando histórico...</p>
            ) : visitasAnteriores.length === 0 ? (
              <p className="chd-vazio">Nenhum atendimento anterior registrado.</p>
            ) : (
              <ul className="chd-historico">
                {visitasAnteriores.map((visita) => (
                  <li key={visita.id} className="chd-historico-item">
                    <span className="chd-historico-campo" title={visita.eventNumber || 'Sem evento'}>
                      <span className="chd-rotulo">Evento</span>
                      {visita.eventNumber || 'Sem evento'}
                    </span>
                    <span className="chd-historico-campo">
                      <span className="chd-rotulo">Entrada</span>
                      {formatarData(visita.entryDate)}
                    </span>
                    <span
                      className={`chd-status ${visita.status === 'ENTREGA' ? 'entregue' : 'em-andamento'}`}
                    >
                      {visita.statusLabel || visita.status || '-'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {restante !== null && (
            <p className="chd-vazio" aria-live="polite">
              Somente consulta. Esta janela fecha sozinha em {restante} s para o telão voltar; Esc ou clicar fora
              também fecha.
            </p>
          )}
        </div>
      </div>

      {/* at-99: controle das esteiras em paralelo, aberto pelo selo ao lado da placa. */}
      {controleParalelasAberto && carId && car && (
        <ControleParalelasModal
          carId={carId}
          plate={car.plate}
          esteiraPrincipalId={car.esteira?.id}
          esteiraPrincipalNome={car.esteira?.nome}
          esteirasParalelas={car.esteirasParalelas}
          onCancel={() => setControleParalelasAberto(false)}
          onAlterado={() => void fetchCarData(carId)}
          sidebarRef={sidebarRef}
        />
      )}
    </div>
  );
};

export default CarHomeDetailsModal;
